import { forwardRef, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ProjectCard from '../ui/ProjectCard';
import { projects } from '../../data/projects';
import './Projects.css';

const Projects = forwardRef((props, ref) => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [sortBy, setSortBy] = useState('latest'); // latest, name, featured

  // Get unique filters from projects data
  const filters = useMemo(() => {
    const allTags = projects.flatMap(project => project.tags || []);
    const uniqueTags = [...new Set(allTags)];
    return ['All', ...uniqueTags];
  }, []);

  // Filter and sort projects
  const filteredProjects = useMemo(() => {
    let filtered = activeFilter === 'All' 
      ? projects 
      : projects.filter(project => 
          project.tags && project.tags.includes(activeFilter)
        );

    // Sort projects
    switch (sortBy) {
      case 'name':
        filtered = [...filtered].sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'featured':
        filtered = [...filtered].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
        break;
      case 'latest':
      default:
        filtered = [...filtered].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
        break;
    }

    return filtered;
  }, [activeFilter, sortBy]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const filterVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  const projectsGridVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };

  return (
    <section id="projects" className="projects" ref={ref}>
      <div className="container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="projects-header"
        >
          <h2 className="section-title">
            <span className="title-accent">My</span> Projects
          </h2>
          <p className="section-subtitle">
            A showcase of my recent work and creative solutions
          </p>
          
          {/* Project Stats */}
          
        </motion.div>

        {/* Controls */}
        <motion.div
          className="projects-controls"
          variants={filterVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {/* Filters */}
          <div className="filter-section">
            <h3 className="control-title">Filter by Category</h3>
            <div className="project-filters">
              {filters.map(filter => (
                <motion.button
                  key={filter}
                  className={`filter-btn ${activeFilter === filter ? 'active' : ''}`}
                  onClick={() => setActiveFilter(filter)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                >
                  <span className="filter-text">{filter}</span>
                  <span className="filter-count">
                    {filter === 'All' 
                      ? projects.length 
                      : projects.filter(p => p.tags && p.tags.includes(filter)).length
                    }
                  </span>
                </motion.button>
              ))}
            </div>
          </div>

          {/* View Controls */}
          <div className="view-controls">
            <div className="sort-control">
              <label htmlFor="sort-select">Sort by:</label>
              <select
                id="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="sort-select"
              >
                <option value="latest">Latest First</option>
                <option value="name">Name A-Z</option>
                <option value="featured">Featured First</option>
              </select>
            </div>

            <div className="view-mode-control">
              <button
                className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Grid View"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="7"/>
                  <rect x="14" y="3" width="7" height="7"/>
                  <rect x="14" y="14" width="7" height="7"/>
                  <rect x="3" y="14" width="7" height="7"/>
                </svg>
              </button>
              <button
                className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                title="List View"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="8" y1="6" x2="21" y2="6"/>
                  <line x1="8" y1="12" x2="21" y2="12"/>
                  <line x1="8" y1="18" x2="21" y2="18"/>
                  <line x1="3" y1="6" x2="3.01" y2="6"/>
                  <line x1="3" y1="12" x2="3.01" y2="12"/>
                  <line x1="3" y1="18" x2="3.01" y2="18"/>
                </svg>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Results Info */}
        <motion.div
          className="results-info"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <span className="results-text">
            Showing {filteredProjects.length} of {projects.length} projects
            {activeFilter !== 'All' && (
              <span className="filter-indicator">
                {' '}in <strong>{activeFilter}</strong>
                <button
                  className="clear-filter"
                  onClick={() => setActiveFilter('All')}
                  title="Clear filter"
                >
                  ×
                </button>
              </span>
            )}
          </span>
        </motion.div>

        {/* Projects Grid/List */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`${activeFilter}-${viewMode}-${sortBy}`}
            className={`projects-container ${viewMode}`}
            variants={projectsGridVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={{ duration: 0.4 }}
          >
            {filteredProjects.length > 0 ? (
              filteredProjects.map((project, index) => (
                <ProjectCard
                  key={`${project.id}-${activeFilter}`}
                  project={project}
                  index={index}
                  viewMode={viewMode}
                />
              ))
            ) : (
              <motion.div
                className="no-projects"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
              >
                <div className="no-projects-icon">🔍</div>
                <h3>No projects found</h3>
                <p>
                  No projects match the selected filter "{activeFilter}".
                  <br />
                  Try selecting a different category.
                </p>
                <button
                  className="reset-filter-btn"
                  onClick={() => setActiveFilter('All')}
                >
                  Show All Projects
                </button>
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Load More Button (if you want pagination) */}
        {filteredProjects.length > 6 && (
          <motion.div
            className="load-more-section"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
          >
            <button className="load-more-btn">
              Load More Projects
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="m6 9 6 6 6-6"/>
              </svg>
            </button>
          </motion.div>
        )}
      </div>
    </section>
  );
});

Projects.displayName = 'Projects';

export default Projects;